/**
 * What a person can do to one work item right now, as the case file offers it.
 *
 * The case file draws a verb only where the route behind it would take it, and
 * the item rail may act only where the page would have offered the same verb.
 * Two copies of those rules would drift, so they live here, pure and total over
 * plain data: the page reads its predicates, and the rail's tool reads
 * `availableItemVerbs` over the same facts it loaded server-side.
 *
 * Every rule is the page's, read in the order the page's dock reads them. The
 * routes stay the authority; this is only the offer.
 */
import { readJobGatePayload } from "./jobGate.js";
import { planQuestionHold } from "./planQuestions.js";
import { GATE_ASK_ACTIONS, } from "./types.js";
/** Every verb the item rail can name, in the order a list of them reads. */
export const ITEM_VERBS = [
    "accept_proposal",
    "decline_proposal",
    "approve_plan",
    "send_back_plan",
    "accept_plan_changes",
    "reject_plan_change",
    "answer",
    "dismiss_ask",
    "stop_to_planning",
    "reoffer_plan",
    "continue_anyway",
    "patch_plan",
    "approve_act",
    "decline_act",
    "start_planning",
    "request_changes",
    "fix_conflicts",
    "run_again",
    "stop_run",
    "hand_back",
    "leave_next_step_note",
    "return_to_proposed",
    "start_over",
    "close_item",
    "split_item",
    "rerun_review",
    "review_again",
    "edit_fields",
];
/** The phases that hold work a person can throw away and start over. */
const START_OVER_PHASES = new Set([
    "accepted",
    "planning",
    "awaiting_execution_approval",
    "executing",
    "needs_input",
    "final_review",
]);
/* ── the gate's ask (`payload.ask`) ─────────────────────────────────────────── */
const ASK_ACTIONS = new Set(GATE_ASK_ACTIONS);
/** The gate's ask, when its payload carries a readable one. Total: any other shape is null. */
export function readGateAsk(payload) {
    const raw = payload?.ask;
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return null;
    const ask = raw;
    if (typeof ask.headline !== "string" || !ask.headline.trim())
        return null;
    if (typeof ask.need !== "string" || !Array.isArray(ask.facts))
        return null;
    const facts = ask.facts.flatMap((entry) => {
        if (!entry || typeof entry !== "object")
            return [];
        const fact = entry;
        if (typeof fact.text !== "string" || !fact.text.trim())
            return [];
        const tone = fact.tone === "ok" || fact.tone === "bad" ? fact.tone : "info";
        return [
            {
                tone,
                text: fact.text,
                ...(typeof fact.detail === "string" && fact.detail.trim() ? { detail: fact.detail } : {}),
                ...(typeof fact.excerpt === "string" && fact.excerpt.trim()
                    ? { excerpt: fact.excerpt }
                    : {}),
                ...(typeof fact.href === "string" && isExternalHttpsUrl(fact.href)
                    ? { href: fact.href }
                    : {}),
            },
        ];
    });
    const rec = ask.recommended;
    const recommended = rec &&
        typeof rec.action === "string" &&
        ASK_ACTIONS.has(rec.action) &&
        typeof rec.why === "string"
        ? { action: rec.action, why: rec.why }
        : null;
    const hide = Array.isArray(ask.hide)
        ? ask.hide.filter((entry) => typeof entry === "string" && ASK_ACTIONS.has(entry))
        : [];
    const link = readAskLink(ask.link);
    const patch = readAskPatch(ask.patch);
    return {
        headline: ask.headline,
        facts,
        need: ask.need,
        recommended,
        ...(hide.length ? { hide } : {}),
        ...(link ? { link } : {}),
        ...(patch ? { patch } : {}),
        ...(ask.continueAnyway === true ? { continueAnyway: true } : {}),
        ...(ask.stopClass === "infra" || ask.stopClass === "person"
            ? { stopClass: ask.stopClass }
            : {}),
        ...(typeof ask.sentence === "string" && ask.sentence.trim() ? { sentence: ask.sentence } : {}),
    };
}
/**
 * A link the ask may carry: an app path only. One leading slash and no
 * second, so a stored payload can never point the reader off the app.
 */
export function readAskLink(raw) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return null;
    const link = raw;
    if (typeof link.label !== "string" || !link.label.trim())
        return null;
    if (typeof link.href !== "string" || !isAppPath(link.href))
        return null;
    return { label: link.label, href: link.href };
}
export function isAppPath(href) {
    return href.startsWith("/") && !href.startsWith("//") && !href.includes("\\") && !/\s/.test(href);
}
/**
 * A fact's link leaves the app on purpose (a check's page on GitHub), so it
 * is held to one shape: `https://`, a plain host (letters, digits, dots,
 * dashes, an optional port) and a path with no whitespace or backslash.
 * Userinfo (`evil.example\\@github.com`, which a browser reads as host
 * `evil.example`) and every other scheme are refused, so a stored payload can
 * neither run a script nor dress a link up as another host.
 */
export function isExternalHttpsUrl(href) {
    return /^https:\/\/[A-Za-z0-9.-]+(?::\d{1,5})?(?:[/?#][^\s\\]*)?$/.test(href);
}
function readAskPatch(raw) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return null;
    const patch = raw;
    if (patch.kind !== "add_files" && patch.kind !== "use_checks")
        return null;
    if (!Array.isArray(patch.values))
        return null;
    const values = patch.values.filter((value) => typeof value === "string" && value.trim() !== "");
    return values.length ? { kind: patch.kind, values } : null;
}
/**
 * Is this park a paused RUN, or an ordinary ask?
 *
 * The difference decides which footer an editor gets, so it is read off the
 * data rather than guessed from the phase. Every needs_input gate sits at
 * `phase: 'needs_input'`, including a Slack question and a compliance chase.
 * `pausedRunId` is written by the parks that stopped a run and by no others,
 * which makes it the honest signal: a run stopped, and it is waiting on this.
 */
export function isPausedRunAsk(gate) {
    return gate.kind === "needs_input" && typeof gate.payload?.pausedRunId === "string";
}
/**
 * A draft pull request that did not open: the ask that lists it in Needs you.
 * The approved publication beside it is how it is answered (Retry, or Reopen
 * plan approval), never a prose answer.
 */
export function isPublicationAsk(gate) {
    const publication = gate.payload?.publication;
    return (gate.kind === "needs_input" &&
        !!publication &&
        typeof publication === "object" &&
        typeof publication.pendingActionId === "string");
}
/** The `source` a repository ask carries on its gate's payload. */
export const REPO_CHOICE_ASK_SOURCE = "choose_repo";
/**
 * A ticket the Team accepted on its own and could not tell which of its
 * repositories it builds in: the ask that lists it in Needs you. Picking a
 * repository answers it and starts planning, never a prose answer.
 */
export function isRepoChoiceAsk(gate) {
    return gate?.kind === "needs_input" && gate.payload?.source === REPO_CHOICE_ASK_SOURCE;
}
/** The one plan-preserving resume phase: answering re-offers the approved plan. */
export const PLAN_PRESERVING_RESUME_PHASE = "awaiting_execution_approval";
/**
 * The resumable-infra-stop rule, in ONE place: a needs_input stop whose payload
 * says the machine failed and the approved plan survived, so answering
 * re-offers that same plan. The paused-run card's `infra_stop` arm and the
 * generic footer's Run-it-again verb both read this.
 */
export function resumesApprovedPlan(gate) {
    return gate.kind === "needs_input" && gate.payload?.resumePhase === PLAN_PRESERVING_RESUME_PHASE;
}
/** Why a build ticket stopped when its pull request closed on GitHub without a merge. */
export const PR_CLOSED_UNMERGED_STOP_REASON = "pr_closed_unmerged";
/**
 * The closed-pull-request stop: the plan survived, so it runs again like any
 * resumable stop, and unlike one it may also be closed, since a person who
 * closed the pull request may not want the work at all.
 */
export function isClosedPullRequestStop(gate) {
    return resumesApprovedPlan(gate) && gate.payload?.stopReason === PR_CLOSED_UNMERGED_STOP_REASON;
}
export function needsInputStopKindOf(resumePhase, stopReason, preservedCandidate) {
    if (resumePhase === PLAN_PRESERVING_RESUME_PHASE)
        return "infra_stop";
    if (stopReason === "candidate_outside_scope" || preservedCandidate)
        return "scope_stop";
    if (resumePhase === "planning" && stopReason)
        return "plan_stop";
    return "paused";
}
/** The stop kind read off the gate's own payload. */
export function gateStopKind(gate) {
    const payload = gate.payload ?? {};
    return needsInputStopKindOf(typeof payload.resumePhase === "string" ? payload.resumePhase : null, typeof payload.stopReason === "string" ? payload.stopReason : null, payload.preservedCandidate === true);
}
/**
 * "Offer the plan again": only on a plan-class stop, where the primary verb
 * revises the plan, and never when the side that stopped hid it because it
 * would fail the same way.
 */
export function canReofferPlan(gate) {
    if (!isPausedRunAsk(gate) || gateStopKind(gate) !== "plan_stop")
        return false;
    return !(readGateAsk(gate.payload)?.hide ?? []).includes("reoffer");
}
/**
 * "Continue anyway (+1 attempt)": only on a stopped build whose ask offers it
 * (once per approval, decided where the ask was written), and never on a
 * repository that was removed, where no build can load.
 */
export function canContinueAnyway(gate, item = null) {
    if (!isPausedRunAsk(gate) || gateStopKind(gate) === "paused" || item?.removedRepoRef)
        return false;
    return readGateAsk(gate.payload)?.continueAnyway === true;
}
/** The stop's one-click plan change: offered only when the ask carries one. */
export function canPatchPlan(gate, item = null) {
    if (!isPausedRunAsk(gate) || gateStopKind(gate) === "paused" || item?.removedRepoRef)
        return false;
    return readGateAsk(gate.payload)?.patch != null;
}
/* ── the stop's recommended act ─────────────────────────────────────────────── */
/**
 * The button words for each act an ask can recommend, in ONE place: the sheet
 * draws the button with them, and the bell, the Slack DM and the email name
 * the same button. `infraStop`: the stop resumes the approved plan, so the
 * answer the ask wants is running it again.
 */
const STOP_ACT_LABELS = {
    continue_anyway: () => "Continue anyway (+1 attempt)",
    patch_plan: (ask) => {
        if (ask.patch?.kind === "use_checks")
            return "Use these checks and continue";
        const n = ask.patch?.values.length ?? 0;
        return n === 1 ? "Add this file and continue" : `Add these ${n} files and continue`;
    },
    reoffer: () => "Run the plan again",
    answer: (_ask, infraStop) => (infraStop ? "Run again" : "Send answer and resume"),
    stop: () => "Keep for a human",
    take_over: () => "Take it over",
};
/** The button the ask recommends, in words. Null when it recommends none. */
export function stopActLabel(ask, opts) {
    const action = ask?.recommended?.action;
    return ask && action ? STOP_ACT_LABELS[action](ask, opts.infraStop) : null;
}
/**
 * The ask's recommended act, when it is one of the stop's one-click ways on
 * and the rules above would offer it here: Continue anyway and the plan patch
 * where the ask carries them, the plan run again on a stop that keeps the
 * approved plan or a plan stop that may offer it. Never an act the ask hides.
 */
export function recommendedStopAct(gate) {
    const payload = gate.payload && typeof gate.payload === "object" && !Array.isArray(gate.payload)
        ? gate.payload
        : null;
    const shape = { kind: gate.kind, payload };
    const ask = readGateAsk(payload);
    const action = ask?.recommended?.action;
    if (!ask || !action || (ask.hide ?? []).includes(action))
        return null;
    switch (action) {
        case "continue_anyway":
            return canContinueAnyway(shape) ? action : null;
        case "patch_plan":
            return canPatchPlan(shape) ? action : null;
        case "reoffer":
            return resumesApprovedPlan(shape) || canReofferPlan(shape) ? action : null;
        default:
            return null;
    }
}
/* ── the plan gate ──────────────────────────────────────────────────────────── */
function jobReadOf(gate) {
    return readJobGatePayload(gate.payload);
}
/** Is this the plan gate, however it was minted? */
export function isPlanAsk(gate) {
    return gate.kind === "approve_execution_plan" || jobReadOf(gate)?.act === "proceed_to_build";
}
/** The plan's evidence hold, as the gate payload carries it. Null when the evidence carries itself. */
export function planEvidenceHold(gate) {
    const hold = gate.payload?.humanApprovalRequired;
    if (!hold || typeof hold !== "object" || Array.isArray(hold))
        return null;
    const reason = hold.reason;
    if (typeof reason !== "string")
        return null;
    const gap = hold.researchGap;
    const question = gap && typeof gap === "object" && !Array.isArray(gap)
        ? gap.question
        : null;
    const suggested = hold.suggestedChecks;
    return {
        reason,
        question: typeof question === "string" && question.trim() ? question.trim() : null,
        suggestedChecks: Array.isArray(suggested)
            ? suggested.filter((entry) => typeof entry === "string" && entry.trim() !== "")
            : [],
    };
}
export function gatePrimaryBlock(gate, questions) {
    if (jobReadOf(gate)?.act && gate.actAnswerable === false)
        return "act_elsewhere";
    if (!isPlanAsk(gate))
        return null;
    const held = planEvidenceHold(gate)?.reason;
    if (held === "plan_no_files")
        return "plan_no_files";
    if (held === "plan_placeholder_files")
        return "plan_placeholder_files";
    if ((questions.applying ?? 0) > 0)
        return "plan_edit_in_progress";
    if ((questions.failed ?? 0) > 0)
        return "plan_answer_failed";
    if ((questions.pendingChanges ?? 0) > 0)
        return "plan_changes_pending";
    if (questions.answered < questions.total)
        return "questions_open";
    return null;
}
/** The plan the gate asks about: the gate row's, else the payload's. */
function gatePlanId(gate) {
    if (gate.planId)
        return gate.planId;
    return typeof gate.payload?.planId === "string" ? gate.payload.planId : null;
}
/**
 * What `gatePrimaryBlock` weighs at a gate, read off the verb state: the
 * plan's questions, its inputs not in the plan yet, and the Ask changes
 * waiting. The page's footer and the rail read Approve's hold through it.
 */
export function planGateHold(gate, state) {
    return {
        ...planQuestionHold(state.planQuestions ?? []),
        applying: state.planInputs?.applying ?? 0,
        failed: state.planInputs?.failed ?? 0,
        pendingChanges: pendingPlanAmendmentIds(gate).length,
    };
}
/** The changes proposed against the plan that still wait on the reviewer. */
export function pendingPlanAmendmentIds(gate) {
    if (gate.kind !== "approve_execution_plan")
        return [];
    const raw = gate.payload?.planAmendments;
    if (!Array.isArray(raw))
        return [];
    return raw.flatMap((entry) => {
        if (!entry || typeof entry !== "object")
            return [];
        const row = entry;
        if (typeof row.id !== "string" || row.status !== "pending")
            return [];
        if (typeof row.rationale !== "string" || typeof row.proposedAt !== "string")
            return [];
        return [row.id];
    });
}
/* ── runs ───────────────────────────────────────────────────────────────────── */
/** The run statuses that read as live (the page's running tone). */
const LIVE_RUN_STATUSES = new Set(["scoping", "running"]);
export function isLiveRun(run) {
    return LIVE_RUN_STATUSES.has(run.status);
}
/**
 * The statuses the stop route refuses: the run already ended, or it is parked
 * (`blocked`) with nothing in motion to stop. The route reads this same list.
 */
export const STOP_REFUSED_RUN_STATUSES = [
    "complete",
    "partial",
    "failed",
    "dismissed",
    "blocked",
];
/** Can Stop land on this run? Only while it has not ended. */
export function canStopRun(run) {
    return !STOP_REFUSED_RUN_STATUSES.includes(run.status);
}
/**
 * Does the run have something Run again can start from? The route's
 * sources, in its order: a one-attempt lane attempt whose plan was approved
 * (the same attempt resumes on its own branch, `attempts/runAgain.ts`), the
 * line (job) behind the run, else an approved build (an execution run that
 * carries its plan). A planning, scoping or review pass has none, and the
 * route refuses it. A missing field reads as none.
 */
export function runHasRestartSource(run) {
    if (run.lane === "attempt")
        return run.planApproved === true;
    if (run.jobId)
        return true;
    return Boolean(run.planId) && (run.purpose ?? "execution") === "execution";
}
/** The run statuses a run stopped short of its work in: what Run again is offered on. */
const STOPPED_RUN_STATUSES = new Set(["failed", "partial", "dismissed"]);
/**
 * A one-attempt lane attempt that ended without finishing: stopped with no
 * ask open (its pull request closed on GitHub, its session aborted, a
 * person's Stop). Its pull request, if any, is not waiting on review, so the
 * merge dock's verbs are not its.
 */
export function isStoppedLaneRun(run) {
    return run?.lane === "attempt" && STOPPED_RUN_STATUSES.has(run.status);
}
/**
 * Which runs carry Run again: stopped or failed only, never a run that
 * finished and never one still parked (`blocked`), and only when the route has
 * something to start from. The route is still the authority; this stops the
 * page offering a verb it knows will be refused.
 */
export function canRunAgain(run) {
    return STOPPED_RUN_STATUSES.has(run.status) && runHasRestartSource(run);
}
/**
 * Can the person holding this ticket hand it back to Wright? A person holds it
 * once they took a run over (`assigneeType` human). Hand back returns it to
 * the Team and starts the one thing Wright picks up from there: a fix round on
 * its pull request in Review, the stopped build's plan offered again, or
 * Reeve's plan on a ticket that was never planned. So it needs one of those,
 * and nothing live or asked that the start would collide with. Planning
 * refuses a ticket a person holds, so a held ticket at Plan has no other way
 * to the Team.
 */
export function canHandBack(state) {
    const { item, openGate, runs } = state;
    if (item.assigneeType !== "human" || item.archivedAt || openGate)
        return false;
    if (TERMINAL_PHASES.has(item.phase) || item.currentRunId || runs.some(isLiveRun))
        return false;
    if (item.phase === "final_review")
        return item.kind !== "review" && Boolean(item.prUrl);
    if (isHeldBeforeBuild(item, runs))
        return true;
    const latest = runs[0];
    // Hand back re-offers a legacy build's plan; a lane attempt is taken up by Run again.
    return (latest != null &&
        latest.lane !== "attempt" &&
        (latest.purpose ?? "execution") === "execution" &&
        canRunAgain(latest));
}
/**
 * A person holds a ticket that has not reached Build: accepted, or planning
 * with no build behind it. Hand back gives it to Reeve to plan; nothing else
 * can, since planning refuses a ticket a person holds.
 */
export function isHeldBeforeBuild(item, runs) {
    if (item.assigneeType !== "human")
        return false;
    if (item.phase !== "accepted" && item.phase !== "planning")
        return false;
    return !runs.some((run) => (run.purpose ?? "execution") === "execution");
}
/** The refusal's code, for a route or a tool that switches on it. */
export const KEPT_WORK_REFUSAL_CODE = "kept_work";
/**
 * The work the latest attempt of a one-attempt-lane ticket kept, or null when
 * it kept none: commits pushed to its branch, or a pull request (a merged one
 * finishes the ticket, so one still here did not merge). Starting a plan
 * begins a new attempt on a new branch, which opens a new pull request, so
 * over kept work it would leave that work behind without a word. Start over
 * is the act that throws work away, and it asks whether to close the pull
 * request and delete the branch. The one rule behind both the offer
 * (`canStartItemPlan`) and the start's refusal (`attempts/start.ts`). A legacy
 * ticket keeps its own rules.
 */
export function laneKeptWork(facts) {
    if (facts.lane !== "attempt")
        return null;
    const prNumber = facts.prNumber ?? null;
    const prUrl = facts.prUrl || null;
    if (!facts.commitSha && prNumber == null && !prUrl)
        return null;
    return { branch: facts.branchName || null, prNumber, prUrl };
}
/** Why a plan does not start over kept work, naming Start over and the ways to keep it. */
export function keptWorkPlanRefusal(kept) {
    const branch = kept.branch ? `branch ${kept.branch}` : "its branch";
    const pullRequest = kept.prNumber != null
        ? `pull request #${kept.prNumber}`
        : kept.prUrl
            ? "its pull request"
            : null;
    const work = pullRequest ? `${pullRequest} and ${branch}` : `the commits pushed to ${branch}`;
    return ("Starting a plan would begin a new attempt on a new branch with its own pull request, " +
        `and leave the last attempt's ${work} behind. ` +
        "To throw that work away, use Start over: it starts the ticket again and asks whether to " +
        "close the pull request and delete the branch. To keep the work, carry on with that " +
        "attempt from the ticket, or close the ticket.");
}
/* ── starting the plan ──────────────────────────────────────────────────────── */
export function isUnassignedItem(item) {
    return item.assigneeId == null && !item.assigneeName;
}
export function isTeamOwnedItem(item) {
    if (item.deskId == null)
        return false;
    if (item.assigneeType !== "human")
        return true;
    return (isUnassignedItem(item) &&
        item.personaSlug === "bailey" &&
        item.sourceRef?.lane === "proposal_lane");
}
/**
 * Can a person start (or restart) the plan from here? `accepted` is the phase
 * planning starts from. `planning` with no run alive is the same fact under
 * another name: the phase was written by a create or a stale update, never by
 * a planning run, and nothing schedules planning for it (the queue rhythm
 * starts only `accepted` items), so the one way forward is a person pressing
 * the verb. The start-planning route accepts both phases. Never over work
 * the latest attempt kept ({@link laneKeptWork}): the start refuses it.
 */
export function canStartItemPlan(item, hasPreviousPlanner) {
    const startable = item.phase === "accepted" || item.phase === "planning";
    if (!startable || item.deskId == null || item.currentRunId)
        return false;
    if (laneKeptWork(item))
        return false;
    if (!isTeamOwnedItem(item) && !isUnassignedItem(item))
        return false;
    // A lane bet or a repo-bearing item always gets the verb: /start-planning
    // takes any accepted item with no gate and no run. A repo-less item outside
    // the lane that already has a due pass is the Team's to pick up, not the
    // person's to start.
    if (hasPreviousPlanner || item.repoId != null)
        return true;
    if (item.sourceRef?.lane === "proposal_lane")
        return true;
    return !item.dueAt;
}
/* ── the whole offer ────────────────────────────────────────────────────────── */
/** The gate kinds whose footer draws verbs (and so the plan's More menu beside them). */
const FOOTER_GATE_KINDS = new Set([
    "accept_proposal",
    "approve_execution_plan",
    "needs_input",
    "review_result",
    "commission_project",
    "milestone_decomposition",
]);
/** The acts the rail may answer on a Job's gate. Never a merge, never a PR approval. */
const RAIL_JOB_ACTS = new Set([
    "accept_proposal",
    "proceed_to_build",
    "push_fix",
]);
function dockOf(state, liveRun) {
    const { item, openGate } = state;
    if (item.archivedAt)
        return "archived";
    // The gate first, as the sheet's footer reads it: an open linked card beside
    // a gate never hides the gate's verbs. The one exception is the publication
    // ask, which the publication card beside it answers.
    if (openGate && !(isPublicationAsk(openGate) && state.hasOpenPendingAction))
        return "gate";
    if (state.hasOpenPendingAction || state.hasOpenRunDecision)
        return "panel";
    if (item.kind !== "review" && item.prUrl && item.stage === "review" && !liveRun) {
        // A lane attempt that stopped keeps its Review phase, but its pull request
        // waits on nothing (it may be closed): Run again, not a review round.
        return isStoppedLaneRun(state.runs[0] ?? null) ? "stopped_attempt" : "merge";
    }
    if (item.phase === "proposed")
        return "proposal";
    if (item.isActivation)
        return "activation";
    if (item.phase === "accepted" || item.phase === "planning" || item.phase === "executing") {
        return "in_flight";
    }
    return "neutral";
}
function addGateVerbs(gate, state, out) {
    // A paused run gets its own card: answer, stop, and on a plan-class stop the
    // plan offered again.
    if (isPausedRunAsk(gate)) {
        out.add("answer");
        out.add("stop_to_planning");
        if (canReofferPlan(gate))
            out.add("reoffer_plan");
        if (canContinueAnyway(gate, state.item))
            out.add("continue_anyway");
        if (canPatchPlan(gate, state.item))
            out.add("patch_plan");
        return;
    }
    const job = jobReadOf(gate);
    // A non-member answers neither half of a Job's gate.
    if (job && state.viewerIsTeamMember === false)
        return;
    const primaryOpen = gatePrimaryBlock(gate, planGateHold(gate, state)) == null;
    // The fix round's ask is keyed off the act, whatever gate it rides.
    if (job?.act === "push_fix") {
        if (primaryOpen)
            out.add("approve_act");
        out.add("decline_act");
        // The person's own instruction starts the round now and answers the ask,
        // except over a built change waiting for its push: a second build would
        // race it to the branch.
        if (job.evidence.kind !== "patch_ready")
            out.add("request_changes");
        // Wright's "ready" ask is answered by a merge round too; the server
        // refuses when the pull request does not conflict.
        if (job.evidence.askKind === "ready_for_person")
            out.add("fix_conflicts");
        return;
    }
    switch (gate.kind) {
        case "accept_proposal":
            if (primaryOpen)
                out.add("accept_proposal");
            out.add("decline_proposal");
            return;
        case "approve_execution_plan":
            if (primaryOpen && gatePlanId(gate))
                out.add("approve_plan");
            out.add("send_back_plan");
            if (pendingPlanAmendmentIds(gate).length > 0) {
                out.add("accept_plan_changes");
                out.add("reject_plan_change");
            }
            return;
        case "needs_input":
            // A plain question: answer it, or say it is not needed, which closes the
            // item. An infra stop's one verb is running the plan again.
            // A closed pull request's stop also says the work may not be wanted.
            // A repository ask is answered by setting the ticket's repository
            // (`edit_fields`), never in prose.
            if (primaryOpen && !isRepoChoiceAsk(gate))
                out.add("answer");
            if (!resumesApprovedPlan(gate) || isClosedPullRequestStop(gate))
                out.add("dismiss_ask");
            return;
        case "job_act":
            if (!job?.act || !RAIL_JOB_ACTS.has(job.act))
                return;
            if (primaryOpen)
                out.add("approve_act");
            out.add("decline_act");
            return;
        default:
            return;
    }
}
function gateFooterHasVerbs(gate) {
    return Boolean(jobReadOf(gate)?.act) || FOOTER_GATE_KINDS.has(gate.kind);
}
/**
 * Start over is offered almost always: wherever there is work to throw away
 * (planned, building, stopped, in review), to a person on the Team, except on
 * a review of someone else's pull request, an archived ticket and the
 * activation ticket. Proposed has nothing to throw away; done, closed and
 * split are over.
 */
export function canStartOver(state) {
    const { item } = state;
    return (START_OVER_PHASES.has(item.phase) &&
        item.kind !== "review" &&
        !item.archivedAt &&
        item.isActivation !== true &&
        state.viewerIsTeamMember !== false);
}
/**
 * The phases a ticket's work is over in: nothing acts on it but an edit, and
 * on a split original Back to Proposed (`availableItemVerbs`).
 */
const TERMINAL_PHASES = new Set(["done", "dismissed", "split"]);
/**
 * A review case's own verbs: Proctor looks again at an open pull request, or
 * a merged or closed one gets a fresh review on a new case.
 */
function addReviewCaseVerbs(item, out) {
    if (item.kind !== "review" || !item.reviewPr)
        return;
    if (item.reviewPr === "open" && item.reviewPrLive !== true)
        out.add("rerun_review");
    if (item.reviewPr === "merged" || item.reviewPr === "closed")
        out.add("review_again");
}
/**
 * Every verb this reader can act on this item with right now, in `ITEM_VERBS`
 * order: every verb the case file's footer and More menu offer (save the Held
 * buttons, `HELD_PAGE_BUTTONS`), and the chat's own beside them (a note for
 * the next step, an edit of the ticket's fields).
 */
export function availableItemVerbs(state) {
    const { item, openGate } = state;
    const out = new Set();
    // A ticket's fields are the reader's to edit in every state the route takes.
    out.add("edit_fields");
    addReviewCaseVerbs(item, out);
    if (TERMINAL_PHASES.has(item.phase)) {
        // A split original is over save one edge out, which the backend's phase
        // table keeps (`workItems/service.ts`): Back to Proposed, the same
        // correction as on any plan, for a split whose children came out wrong.
        if (item.phase === "split" && !item.archivedAt && state.viewerIsTeamMember !== false) {
            out.add("return_to_proposed");
        }
        return ITEM_VERBS.filter((verb) => out.has(verb));
    }
    const liveRun = state.runs.some(isLiveRun);
    const latest = state.runs[0] ?? null;
    const atPlan = item.phase === "planning" || item.phase === "awaiting_execution_approval";
    if (canHandBack(state))
        out.add("hand_back");
    // The note the next step reads rides the executing body, whatever the dock
    // shows, and any live run past the proposal: a fix round in Review reads it
    // too (YAG-1116). A run on a proposal (Bailey's reading) has no step coming.
    if (item.phase === "executing" || (liveRun && item.phase !== "proposed")) {
        out.add("leave_next_step_note");
    }
    // The escape hatches sit in More beside every dock.
    if (canStartOver(state))
        out.add("start_over");
    if (!item.archivedAt)
        out.add("close_item");
    if (atPlan && !item.archivedAt && state.viewerIsTeamMember !== false)
        out.add("split_item");
    switch (dockOf(state, liveRun)) {
        case "gate":
            if (!openGate)
                break;
            addGateVerbs(openGate, state, out);
            // The plan's More menu sits beside the gate footer's verbs.
            if (atPlan && !isPausedRunAsk(openGate) && gateFooterHasVerbs(openGate)) {
                out.add("return_to_proposed");
            }
            break;
        case "merge":
            // Every round starts from Review (the fix-round preflight refuses
            // anything else). A ticket at `executing` with a pull request still
            // reads as Review, but offering the round there only earns a 409.
            if (item.phase === "final_review") {
                out.add("request_changes");
                out.add("fix_conflicts");
            }
            break;
        case "in_flight": {
            const hasPreviousPlanner = state.hasPreviousPlanner ??
                state.runs.some((run) => run.id === item.latestPlanningRunId || run.purpose === "planning");
            const canStart = !liveRun && canStartItemPlan(item, hasPreviousPlanner);
            if (liveRun)
                out.add("stop_run");
            if (canStart)
                out.add("start_planning");
            // A stopped attempt with no plan verb standing: Run again, and the way
            // back to Proposed beside it. Build has no edge back to Proposed.
            if (!liveRun && !canStart && latest && canRunAgain(latest)) {
                out.add("run_again");
                if (item.phase !== "executing")
                    out.add("return_to_proposed");
            }
            if (item.phase === "planning")
                out.add("return_to_proposed");
            break;
        }
        case "stopped_attempt":
            // The sheet's stopped footer: Run again on the same attempt, with Start
            // over and Close in More beside it.
            if (latest && canRunAgain(latest))
                out.add("run_again");
            break;
        case "proposal":
            // No gate: the sheet's Start plan and Keep for a human, on their gate-less routes.
            out.add("accept_proposal");
            out.add("decline_proposal");
            break;
        default:
            // A run live outside the plan and build phases (a fix round in Review)
            // is stopped from the sheet's working footer all the same.
            if (liveRun && !item.archivedAt)
                out.add("stop_run");
            break;
    }
    return ITEM_VERBS.filter((verb) => out.has(verb));
}
/**
 * The page's buttons that stay on the page (ADR-0051): each one is external
 * or cannot be taken back, so the chat names it and says why instead of
 * pressing it. The chat answers a request for one with this line.
 */
export const HELD_PAGE_BUTTONS = [
    {
        id: "merge",
        label: "Merge",
        why: "it merges on GitHub as the person and cannot be undone",
    },
    {
        id: "confirm_review",
        label: "Confirm review",
        why: "it posts Proctor's review to the pull request on GitHub as the person",
    },
    {
        id: "push_milestone",
        label: "Push to the tracker",
        why: "it writes the milestone's tickets into the tracker",
    },
    {
        id: "commission",
        label: "Commission",
        why: "it puts a whole project's tickets on the Team",
    },
    {
        id: "result_gate",
        label: "Mark done / Request changes on a delivery",
        why: "it settles a delivery with no pull request on the record",
    },
    {
        id: "linked_card",
        label: "the approval card for a staged action or run decision",
        why: "it approves an action that leaves YAGNI, on its own card",
    },
    {
        id: "take_over",
        label: "Take it over",
        why: "it hands the pull request to the person and takes the fix-round line off it",
    },
    {
        id: "job_post",
        label: "Go ahead on a post or publish a Job staged",
        why: "it sends the post outside YAGNI, to Slack, the tracker or GitHub",
    },
];
/** The one sentence the chat says about a Held button. */
export function heldButtonLine(button) {
    return `Held: "${button.label}" is the page's button, because ${button.why}. Press it on the ticket.`;
}
function heldButton(id) {
    return HELD_PAGE_BUTTONS.find((button) => button.id === id);
}
/**
 * The Held buttons this ticket's page shows right now, read off the same
 * facts as the verbs: what the chat names instead of pressing, here.
 */
export function heldPageButtonsOf(state) {
    const { item, openGate } = state;
    const out = [];
    if (item.archivedAt || TERMINAL_PHASES.has(item.phase))
        return out;
    if (state.hasOpenPendingAction || state.hasOpenRunDecision)
        out.push(heldButton("linked_card"));
    if (!openGate) {
        const liveRun = state.runs.some(isLiveRun);
        if (item.kind !== "review" && item.prUrl && item.stage === "review" && !liveRun) {
            out.push(heldButton("merge"));
        }
        return out;
    }
    const job = jobReadOf(openGate);
    if (job?.act === "merge" || job?.act === "approve_pr")
        out.push(heldButton("merge"));
    else if (job?.act === "publish_verdict")
        out.push(heldButton("confirm_review"));
    else if (job?.act && !RAIL_JOB_ACTS.has(job.act))
        out.push(heldButton("job_post"));
    // A capped round and a stopped round both offer Take it over on the page.
    if (job?.act === "push_fix" &&
        (job.cappedByPerItemCap || job.evidence.kind === "round_stopped")) {
        out.push(heldButton("take_over"));
    }
    if (openGate.kind === "review_result") {
        out.push(heldButton(openGate.payload?.heldReviewPayload ? "confirm_review" : "result_gate"));
    }
    if (openGate.kind === "commission_project")
        out.push(heldButton("commission"));
    if (openGate.kind === "milestone_decomposition")
        out.push(heldButton("push_milestone"));
    return out;
}
//# sourceMappingURL=workItemVerbs.js.map